<template>
  <div class="menu--mobile">
    <v-dialog
      class="mobile__menu"
      v-model="displayMobileMenu"
      fullscreen
      :scrim="false"
      transition="dialog-bottom-transition"
      @after-enter="animateItems"
    >
      <template v-slot:activator="{ props }">
        <div class="menu__container">
          <v-icon class="mobile-menu__icon" v-bind="props" aria-label="Open menu">mdi-menu</v-icon>
        </div>
      </template>

      <v-card class="mobile-menu__card">
        <v-card-title>
          <v-row class="mobile-menu__card-title-row">
            <v-col cols="6">
              <h3 class="mobile-menu__title dms400">DIGU</h3>
            </v-col>
            <v-col cols="3" class="close-button-container">
              <v-icon @click="displayMobileMenu = false" class="close-btn" aria-label="Close menu">mdi-close</v-icon>
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text>
          <v-list ref="list" class="mobile-menu__list">
            <!-- Home -->
            <v-list-item density="compact" class="mobile-menu__list-item">
              <v-list-item-text>
                <a class="m400 mobile-menu__link link--primary" href="/" @click.prevent="go('/')">Home</a>
              </v-list-item-text>
            </v-list-item>

            <!-- About Us, Services, Maturity Roadmap, Security Baseline, For Job Seekers -->
            <v-list-item
              v-for="link in primaryLinks"
              :key="link.href"
              density="compact"
              class="mobile-menu__list-item"
            >
              <v-list-item-text>
                <a class="m400 mobile-menu__link link--primary" :href="link.href" @click.prevent="go(link.href)">
                  {{ link.title }}
                </a>
              </v-list-item-text>
            </v-list-item>

            <!--
              Hidden in security mode: For Job Seekers, DIGU Cares, For Businesses, Our Solutions (dropdowns) and
              Packages (/service-packages/custom-web-mobile-ux-ui-design). Routes stay registered.
              Items live in hiddenLegacyDropdowns in src/config/securityNavigation.js; to show
              them again, render them with the dropdown block below.
            -->

            <!-- Resources (the one dropdown) -->
            <v-list-item
              v-for="menu in dropdowns"
              :key="menu.key"
              density="compact"
              class="mobile-menu__list-item"
            >
              <v-list-item-text class="mobile-menu__list-item-text menu-text--secondary">
                <v-expansion-panels style="padding: 0" class="ep">
                  <v-expansion-panel elevation="0" style="background-color: transparent; padding-left: 0">
                    <v-expansion-panel-title>
                      <p class="m400 mobile-menu__link link--primary">{{ menu.title }}</p>
                      <template v-slot:actions="{ expanded }">
                        <v-icon class="mobile-menu__chevron" :icon="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'" />
                      </template>
                    </v-expansion-panel-title>

                    <v-expansion-panel-text class="faq__answer">
                      <v-list class="mobile-menu__list list--secondary">
                        <v-list-item
                          v-for="item in menu.items"
                          :key="item.href"
                          density="compact"
                          class="mobile-menu__list-item"
                        >
                          <v-list-item-text class="mobile-menu__list-item-text menu-text--secondary">
                            <a
                              class="m400 mobile-menu__link link--secondary"
                              :href="item.href"
                              @click.prevent="go(item.href)"
                            >
                              {{ item.title }}
                            </a>
                          </v-list-item-text>
                        </v-list-item>
                      </v-list>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-list-item-text>
            </v-list-item>

            <!-- Replaced by "Score yourself" in security mode.
            <v-list-item density="compact" class="mobile-menu__list-item">
              <v-list-item-text>
                <a class="m400 mobile-menu__link link--primary calendy-link" @click="redirectToCalendly">
                  Schedule a Meeting
                </a>
              </v-list-item-text>
            </v-list-item>
            -->
            <v-list-item density="compact" class="mobile-menu__list-item">
              <v-list-item-text>
                <a
                  class="m400 mobile-menu__link link--primary link--cta"
                  :href="primaryCta.href"
                  @click.prevent="go(primaryCta.href)"
                >
                  {{ primaryCta.title }}
                </a>
              </v-list-item-text>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
// Security-mode copy of partials/MobileMenu.vue: same dialog, classes and styles.
// Links route in-app (no full reload), and the items stagger in with GSAP on open.
import { ref } from 'vue';
import { gsap, prefersReducedMotion } from '@/plugins/gsap';
import { useSmoothNavigate } from '@/composables/security/useSmoothScroll';
import { primaryLinks, primaryCta, dropdowns } from '@/config/securityNavigation';

const displayMobileMenu = ref(false);
const list = ref(null);
const smoothNavigate = useSmoothNavigate();

function go(path) {
  displayMobileMenu.value = false;
  smoothNavigate(path);
}

function animateItems() {
  const el = list.value && (list.value.$el || list.value);
  if (!el || prefersReducedMotion()) return;
  gsap.from(el.querySelectorAll(':scope > .mobile-menu__list-item'), {
    autoAlpha: 0,
    y: 16,
    duration: 0.35,
    ease: 'power2.out',
    stagger: 0.05,
    clearProps: 'all',
  });
}
</script>

<style scoped>
/* Same switch point as SecurityDesktopMenu and the legacy header. */
@media only screen and (min-width: 1282px) {
  .menu--mobile {
    display: none;
  }
}

.mobile-menu__icon {
  color: var(--sec-accent);
  font-size: 2rem;
}

.menu__container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.mobile__menu {
  background-color: var(--sec-mobile-menu-bg);
  backdrop-filter: blur(10px);
}

.mobile-menu__card {
  background: transparent;
  padding: 2rem;
  padding-bottom: 5rem;
}

.mobile-menu__card-title-row {
  justify-content: space-between;
}

.mobile-menu__title {
  font-size: 1.2rem;
  letter-spacing: 0;
  color: var(--sec-mobile-menu-title);
}

@media only screen and (min-width: 394px) {
  .mobile-menu__title {
    font-size: 1.5rem;
  }
}

.close-button-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.close-btn {
  color: var(--sec-mobile-menu-title);
  font-size: 1.5rem !important;
}

.mobile-menu__list {
  background: transparent;
}

.mobile-menu__link {
  text-decoration: none;
  color: var(--sec-mobile-menu-ink);
}

.mobile-menu__chevron {
  color: var(--sec-on-accent);
}

.link--primary {
  font-size: 1.4rem;
  font-weight: 700;
}

.link--cta {
  color: var(--sec-accent);
}

.link--secondary {
  font-size: 1.2rem;
  font-weight: 500;
}

/* keep titles flush left */
.v-expansion-panel-title {
  padding-left: 0 !important;
}

/* --- SPACING SYSTEM --- */
/* Only top-level list items define vertical rhythm */
.mobile-menu__list-item {
  min-height: 68px !important;
  margin-bottom: 1rem; /* single consistent gap between sections */
}

/* Remove extra margins/padding introduced by expansion panels */
.ep,
.ep .v-expansion-panel,
.ep .v-expansion-panel-title {
  margin: 0 !important;
}

.ep .v-expansion-panel-text {
  padding-top: 0 !important;
  padding-bottom: 0 !important; /* prevents double gap after dropdowns */
}

/* Inner (dropdown) list items have tighter spacing and no external margin */
.list--secondary .mobile-menu__list-item {
  margin-bottom: 0 !important;
  min-height: 48px !important;
}

.list--secondary .mobile-menu__list-item:last-child {
  margin-bottom: 0 !important;
}
</style>
